<template>
    <div class="live-page">
        <Spinner v-if="displaySpinner" />
        <Page>
            <template #title>
                <h1 v-html="pageTitle"></h1>
            </template>
            <template #default>
                <div class="live-content">
                    <div v-if="selectedEntity" class="dashboard-container">
                        <component
                            :is="dashboardComponent"
                            :entity-id="selectedEntity.id"
                            :entity-type="selectedEntity.type"
                        />
                    </div>
                    <div v-else class="welcome-message">
                        <p>
                            Select an entity from the tree to view its live
                            metrics.
                        </p>
                    </div>
                </div>
            </template>
        </Page>
    </div>
</template>

<script setup lang="ts">
import { computed, onMounted, ref, watch } from "vue";
import { useRoute } from "vue-router";

import Page from "@/components/Page.vue";
import { useStekManager } from "@/composables/stek-manager";
import { useServices } from "@/lib/openapi";
import DomainDashboard from "@/live/components/Dashboard/DomainDashboard.vue";
import ProcessDashboard from "@/live/components/Dashboard/ProcessDashboard.vue";
import ProductDashboard from "@/live/components/Dashboard/ProductDashboard.vue";
import StekDashboard from "@/live/components/Dashboard/StekDashboard.vue";
import Spinner from "@/shared/Spinner.vue";
import { EntityType } from "@/stores/entity-store";

defineOptions({
    name: "Page",
});

const route = useRoute();
const { stek } = useStekManager();
const api = useServices({
    requires: {
        auth: true,
        organization: true,
        stek: true,
    },
});

const stekName = computed(() => {
    return stek.value?.name || route.params.stekName;
});

const domainName = ref<string | null>(null);
const productName = ref<string | null>(null);
const processName = ref<string | null>(null);

/**
 * Extract array data from API response, handling both direct arrays and wrapped responses.
 */
function extractArrayData<T extends Record<string, unknown>>(
    data: unknown
): Array<T> {
    if (Array.isArray(data)) {
        return data as Array<T>;
    }
    if (
        data &&
        typeof data === "object" &&
        "items" in data &&
        Array.isArray((data as { items: unknown }).items)
    ) {
        return (data as { items: Array<T> }).items;
    }
    return [];
}

/**
 * Get entity identity from a record, handling different identity field names.
 */
function getEntityId(entity: Record<string, unknown>): string {
    return (entity.identity || entity.id || String(entity)) as string;
}

/**
 * Get entity name from a record, handling different name field names.
 */
function getEntityName(entity: Record<string, unknown>): string | null {
    return (entity.name || entity.title || null) as string | null;
}

/**
 * Fetch all domains for the current stek.
 */
async function fetchDomains(): Promise<Array<Record<string, unknown>>> {
    try {
        const result = await api.startDomainDomainMgtGetDomainsByStekStartPost(
            {}
        );
        if (result.response.ok && result.data) {
            return extractArrayData<Record<string, unknown>>(result.data);
        }
    } catch {
        // Failed to fetch domains, ignore
    }
    return [];
}

/**
 * Fetch products for a given domain.
 */
async function fetchProductsByDomain(
    domainId: string
): Promise<Array<Record<string, unknown>>> {
    try {
        const result =
            await api.startProductProductMgtGetProductsByDomainStartPost({
                body: { domain_id: domainId },
            });
        if (result.response.ok && result.data) {
            return extractArrayData<Record<string, unknown>>(result.data);
        }
    } catch {
        // Failed to fetch products, ignore
    }
    return [];
}

/**
 * Fetch processes for a given product.
 */
async function fetchProcessesByProduct(
    productId: string
): Promise<Array<Record<string, unknown>>> {
    try {
        const result =
            await api.startProcessProcessMgtGetProcessesByProductStartPost({
                body: { product_id: productId },
            });
        if (result.response.ok && result.data) {
            return extractArrayData<Record<string, unknown>>(result.data);
        }
    } catch {
        // Failed to fetch processes, ignore
    }
    return [];
}

/**
 * Fetch and set domain name from route params.
 */
async function fetchDomainName(domainId: string): Promise<void> {
    try {
        const domains = await fetchDomains();
        const domain = domains.find((d) => getEntityId(d) === domainId);
        if (domain) {
            domainName.value = getEntityName(domain);
        }
    } catch {
        // Failed to fetch domain name, ignore
    }
}

/**
 * Fetch and set product name from route params.
 */
async function fetchProductName(productId: string): Promise<void> {
    try {
        const domains = await fetchDomains();
        for (const domain of domains) {
            const products = await fetchProductsByDomain(getEntityId(domain));
            const product = products.find((p) => getEntityId(p) === productId);
            if (product) {
                productName.value = getEntityName(product);
                return;
            }
        }
    } catch {
        // Failed to fetch product name, ignore
    }
}

/**
 * Fetch and set process name from route params.
 */
async function fetchProcessName(processId: string): Promise<void> {
    try {
        const domains = await fetchDomains();
        for (const domain of domains) {
            const products = await fetchProductsByDomain(getEntityId(domain));
            for (const product of products) {
                const processes = await fetchProcessesByProduct(
                    getEntityId(product)
                );
                const process = processes.find(
                    (p) => getEntityId(p) === processId
                );
                if (process) {
                    processName.value = getEntityName(process);
                    return;
                }
            }
        }
    } catch {
        // Failed to fetch process name, ignore
    }
}

/**
 * Main function to fetch entity name based on route params.
 */
async function fetchEntityName(): Promise<void> {
    if (!stek.value) return;

    const params = route.params;

    // Reset names
    domainName.value = null;
    productName.value = null;
    processName.value = null;

    if (params.domainId) {
        await fetchDomainName(params.domainId as string);
    } else if (params.productId) {
        await fetchProductName(params.productId as string);
    } else if (params.processId) {
        await fetchProcessName(params.processId as string);
    }
}

const pageTitle = computed(() => {
    const params = route.params;
    if (params.processId) {
        const name = processName.value || params.processId;
        return `<i class="pi pi-share-alt"></i> ${name}`;
    } else if (params.productId) {
        const name = productName.value || params.productId;
        return `<i class="pi pi-box"></i> ${name}`;
    } else if (params.domainId) {
        const name = domainName.value || params.domainId;
        return `<i class="pi pi-cloud"></i> ${name}`;
    } else {
        const name = stekName.value || "Stek";
        return `<i class="pi pi-building"></i> ${name}`;
    }
});

const displaySpinner = ref(false);

// Get selected entity from route params
const selectedEntity = computed(() => {
    if (!stek.value) return null;

    const params = route.params;

    if (params.processId) {
        return {
            id: params.processId as string,
            type: "process", // Use string for entityType to match API
        };
    } else if (params.productId) {
        return {
            id: params.productId as string,
            type: "product", // Use string for entityType to match API
        };
    } else if (params.domainId) {
        return {
            id: params.domainId as string,
            type: "domain", // Use string for entityType to match API
        };
    } else if (stek.value?.identity) {
        return {
            id: stek.value.identity,
            type: "stek", // Use string for entityType to match API
        };
    }

    return null;
});

const dashboardComponent = computed(() => {
    if (!selectedEntity.value) return null;

    switch (selectedEntity.value.type) {
        case "stek":
        case EntityType.STEK:
            return StekDashboard;
        case "domain":
        case EntityType.DOMAIN:
            return DomainDashboard;
        case "product":
        case EntityType.PRODUCT:
            return ProductDashboard;
        case "process":
        case EntityType.PROCESS:
            return ProcessDashboard;
        default:
            return null;
    }
});

watch(
    [
        () => route.params.domainId,
        () => route.params.productId,
        () => route.params.processId,
        stek,
    ],
    () => {
        fetchEntityName();
    },
    { immediate: true }
);

onMounted(() => {
    fetchEntityName();
});
</script>

<style scoped>
.live-page {
    padding: 0;

    :deep(h1) {
        display: flex;
        align-items: center;
        gap: 0.5rem;

        i {
            font-size: 1.2rem;
        }
    }
}

.live-content {
    padding: 0;
    margin: 0;
}

.dashboard-container {
    width: 100%;
}

.welcome-message {
    text-align: center;
    padding: 4rem 2rem;
    color: var(--text-color-secondary, #666);
}
</style>
